import React, { useEffect, useMemo, useState } from "react";
import { getSettings, updateSettings } from "../../../services/settingsServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  FaCloudUploadAlt,
  FaImage,
  FaSave,
  FaTimes,
} from "react-icons/fa";

import {
  uploadReglementHeaderImage,
  uploadReglementFirstImage,
  uploadReglementSecondImage,
  toAbsolutePhotoUrl,
} from "../../../services/settingsPhotoServices";

/* Quill */
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

const FALLBACK = "/img/no-image.jpg";

const withBust = (url, v) => {
  if (!url) return "";
  return `${url}${url.includes("?") ? "&" : "?"}v=${v}`;
};

const getSafeImageSrc = (value) => {
  const src = String(value || "").trim();

  if (!src || src === "null" || src === "undefined" || src === "false") {
    return FALLBACK;
  }

  return src;
};

const handleImageFallback = (event) => {
  const img = event.currentTarget;

  if (img.dataset.fallbackApplied === "true") {
    return;
  }

  img.dataset.fallbackApplied = "true";
  img.removeAttribute("crossorigin");
  img.src = FALLBACK;
};

const isImgTypeOk = (file) =>
  /^image\/(png|jpe?g|webp)$/i.test(file?.type || "");

const isImgSizeOk = (file) => (file?.size || 0) <= 10 * 1024 * 1024;

const ReglementInterieurForm = () => {
  const [settings, setSettings] = useState({
    reglement_interieur: "",
    reglement_interieur_url_header: "",
    reglement_interieur_url_firstimage: "",
    reglement_interieur_url_secondimage: "",
  });

  const [loading, setLoading] = useState(true);

  const [uploadingTarget, setUploadingTarget] = useState(null);
  const uploading = Boolean(uploadingTarget);

  const [selectedHeader, setSelectedHeader] = useState(null);
  const [selectedFirst, setSelectedFirst] = useState(null);
  const [selectedSecond, setSelectedSecond] = useState(null);

  const [imgVersion, setImgVersion] = useState({
    header: 0,
    first: 0,
    second: 0,
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsData = await getSettings();

        if (Array.isArray(settingsData) && settingsData.length > 0) {
          setSettings((prev) => ({
            ...prev,
            ...settingsData[0],
          }));
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
        toast.error("Impossible de charger les paramètres.");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleContentChange = (name, value) => {
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      await updateSettings(settings);
      toast.success("Paramètres mis à jour avec succès");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour des paramètres");
    }
  };

  const previewUrls = useMemo(() => {
    const headerAbs = toAbsolutePhotoUrl(
      settings.reglement_interieur_url_header?.trim() || "",
    );

    const firstAbs = toAbsolutePhotoUrl(
      settings.reglement_interieur_url_firstimage?.trim() || "",
    );

    const secondAbs = toAbsolutePhotoUrl(
      settings.reglement_interieur_url_secondimage?.trim() || "",
    );

    return {
      header: getSafeImageSrc(
        withBust(
          headerAbs || settings.reglement_interieur_url_header || "",
          imgVersion.header,
        ),
      ),
      first: getSafeImageSrc(
        withBust(
          firstAbs || settings.reglement_interieur_url_firstimage || "",
          imgVersion.first,
        ),
      ),
      second: getSafeImageSrc(
        withBust(
          secondAbs || settings.reglement_interieur_url_secondimage || "",
          imgVersion.second,
        ),
      ),
    };
  }, [
    settings.reglement_interieur_url_header,
    settings.reglement_interieur_url_firstimage,
    settings.reglement_interieur_url_secondimage,
    imgVersion.header,
    imgVersion.first,
    imgVersion.second,
  ]);

  const uploadCommonChecks = (file) => {
    if (!file) {
      toast.error("Veuillez sélectionner une image.");
      return false;
    }

    if (!isImgTypeOk(file)) {
      toast.error("Format non supporté. Utilisez PNG / JPG / WEBP.");
      return false;
    }

    if (!isImgSizeOk(file)) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      return false;
    }

    return true;
  };

  const handleUploadHeader = async () => {
    if (uploading) return;
    if (!uploadCommonChecks(selectedHeader)) return;

    setUploadingTarget("header");

    try {
      const data = await uploadReglementHeaderImage(selectedHeader, {
        format: "webp",
      });

      const newUrl = data?.url || data?.imageUrl || "";

      if (!newUrl) {
        toast.error("Upload OK mais URL manquante dans la réponse.");
        return;
      }

      setSettings((prev) => ({
        ...prev,
        reglement_interieur_url_header: newUrl,
      }));

      setImgVersion((v) => ({
        ...v,
        header: v.header + 1,
      }));

      toast.success("Image d’en-tête mise à jour !");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'upload.");
    } finally {
      setSelectedHeader(null);
      setUploadingTarget(null);
    }
  };

  const handleUploadFirstImage = async () => {
    if (uploading) return;
    if (!uploadCommonChecks(selectedFirst)) return;

    setUploadingTarget("first");

    try {
      const data = await uploadReglementFirstImage(selectedFirst, {
        format: "webp",
      });

      const newUrl = data?.url || data?.imageUrl || "";

      if (!newUrl) {
        toast.error("Upload OK mais URL manquante dans la réponse.");
        return;
      }

      setSettings((prev) => ({
        ...prev,
        reglement_interieur_url_firstimage: newUrl,
      }));

      setImgVersion((v) => ({
        ...v,
        first: v.first + 1,
      }));

      toast.success("Première image mise à jour !");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'upload.");
    } finally {
      setSelectedFirst(null);
      setUploadingTarget(null);
    }
  };

  const handleUploadSecondImage = async () => {
    if (uploading) return;
    if (!uploadCommonChecks(selectedSecond)) return;

    setUploadingTarget("second");

    try {
      const data = await uploadReglementSecondImage(selectedSecond, {
        format: "webp",
      });

      const newUrl = data?.url || data?.imageUrl || "";

      if (!newUrl) {
        toast.error("Upload OK mais URL manquante dans la réponse.");
        return;
      }

      setSettings((prev) => ({
        ...prev,
        reglement_interieur_url_secondimage: newUrl,
      }));

      setImgVersion((v) => ({
        ...v,
        second: v.second + 1,
      }));

      toast.success("Deuxième image mise à jour !");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'upload.");
    } finally {
      setSelectedSecond(null);
      setUploadingTarget(null);
    }
  };

  if (loading) {
    return (
      <section className="w-full">
        <div className="mx-auto max-w-7xl px-1 sm:px-1">
          <div className="overflow-hidden rounded-2xl bg-white/70 shadow ring-1 ring-slate-200/70 backdrop-blur dark:bg-slate-900/60 dark:ring-slate-700/70">
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8">
              <div className="h-8 w-64 animate-pulse rounded bg-white/25" />
            </div>

            <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-2">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-24 animate-pulse rounded-xl bg-slate-100/80 dark:bg-slate-800/40"
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-1 sm:px-1">
        <div className="overflow-hidden rounded-2xl bg-white/70 shadow ring-1 ring-slate-200/70 backdrop-blur dark:bg-slate-900/60 dark:ring-slate-700/70">
          <div className="flex flex-col gap-4 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
                Règlement intérieur — Contenu & Images
              </h2>

              <p className="mt-1 text-sm text-white/90 sm:text-[15px]">
                Éditez le texte et gérez les visuels associés : image d’en-tête
                et images de section.
              </p>
            </div>

            <Button
              text="Enregistrer"
              icon={FaSave}
              onClick={handleSubmit}
              className="!rounded-xl !bg-white !text-slate-900 !shadow-md !hover:bg-white/90 gap-2"
            />
          </div>

          <div className="space-y-6 p-4 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="rounded-xl border border-slate-200 bg-white/70 p-4 dark:border-slate-800 dark:bg-slate-900/40 sm:p-5">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Texte du règlement intérieur
                </label>

                <div
                  className="mt-1
                    [&_.ql-toolbar]:rounded-t-xl [&_.ql-toolbar]:border-slate-300 dark:[&_.ql-toolbar]:border-slate-700
                    [&_.ql-toolbar]:bg-white/80 dark:[&_.ql-toolbar]:bg-slate-900/70
                    [&_.ql-container]:rounded-b-xl [&_.ql-container]:border-slate-300 dark:[&_.ql-container]:border-slate-700
                    [&_.ql-container]:bg-white/80 dark:[&_.ql-container]:bg-slate-900/70"
                >
                  <ReactQuill
                    value={settings.reglement_interieur || ""}
                    onChange={(v) =>
                      handleContentChange("reglement_interieur", v)
                    }
                    theme="snow"
                    formats={formats}
                    modules={modules}
                    placeholder="<p>Rédigez ici le règlement intérieur…</p>"
                  />
                </div>

                <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                  Astuce : utilisez des titres H1/H2 et des listes pour
                  structurer les sections.
                </p>
              </div>

              <div className="space-y-5">
                <ImageUploadCard
                  title="Image d’en-tête"
                  hint="Grand visuel bannière recommandé : format paysage large, par exemple 1920×600."
                  currentImage={previewUrls.header}
                  selectedImage={selectedHeader}
                  onSelect={setSelectedHeader}
                  onSave={handleUploadHeader}
                  isUploading={uploadingTarget === "header"}
                  disabled={uploading && uploadingTarget !== "header"}
                  featured
                />

                <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                  <ImageUploadCard
                    title="Première image"
                    hint="Visuel illustrant la première section du règlement, idéalement en paysage."
                    currentImage={previewUrls.first}
                    selectedImage={selectedFirst}
                    onSelect={setSelectedFirst}
                    onSave={handleUploadFirstImage}
                    isUploading={uploadingTarget === "first"}
                    disabled={uploading && uploadingTarget !== "first"}
                  />

                  <ImageUploadCard
                    title="Deuxième image"
                    hint="Visuel illustrant la deuxième section du règlement, idéalement en paysage."
                    currentImage={previewUrls.second}
                    selectedImage={selectedSecond}
                    onSelect={setSelectedSecond}
                    onSave={handleUploadSecondImage}
                    isUploading={uploadingTarget === "second"}
                    disabled={uploading && uploadingTarget !== "second"}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  text="Mettre à jour"
                  type="submit"
                  icon={FaSave}
                  className="!rounded-xl gap-2"
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        input::placeholder {
          color: #94a3b8;
        }

        .dark input::placeholder {
          color: #94a3b8;
          opacity: .7;
        }

        .dark .ql-editor strong,
        .dark .ql-editor b {
          color: #fff;
        }

        html {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
      `}</style>
    </section>
  );
};

const ImageUploadCard = ({
  title,
  hint,
  currentImage,
  selectedImage,
  onSelect,
  onSave,
  isUploading,
  disabled,
  featured = false,
}) => {
  const [localPreview, setLocalPreview] = useState("");

  useEffect(() => {
    if (!selectedImage) {
      setLocalPreview("");
      return undefined;
    }

    const url = URL.createObjectURL(selectedImage);
    setLocalPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [selectedImage]);

  const previewSrc = useMemo(() => {
    return getSafeImageSrc(localPreview || currentImage);
  }, [localPreview, currentImage]);

  return (
    <div
      className={`overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm transition dark:border-slate-800 dark:bg-slate-900 ${
        disabled ? "pointer-events-none opacity-60" : ""
      }`}
    >
      <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-4 dark:border-slate-800 dark:bg-slate-950/40 sm:px-5">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
            <FaImage />
          </span>

          <div>
            <h4 className="text-base font-bold text-slate-900 dark:text-white">
              {title}
            </h4>

            <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-400">
              {hint}
            </p>
          </div>
        </div>
      </div>

      <div
        className={`grid gap-5 p-4 sm:p-5 ${
          featured
            ? "xl:grid-cols-[minmax(0,1fr),340px]"
            : "lg:grid-cols-[minmax(0,1fr),260px]"
        }`}
      >
        <div>
          <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
            Sélectionner une image
          </label>

          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(event) => onSelect(event.target.files?.[0] ?? null)}
            disabled={isUploading}
            className="block w-full rounded-2xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition file:mr-3 file:rounded-xl file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-bold file:text-indigo-700 hover:file:bg-indigo-100 focus:ring-2 focus:ring-violet-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-100 dark:file:bg-indigo-500/10 dark:file:text-indigo-300"
          />

          {selectedImage ? (
            <div className="mt-3 rounded-2xl bg-slate-50 px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-800">
              <span className="font-bold">{selectedImage.name}</span>
              <span> — {Math.round(selectedImage.size / 1024)} Ko</span>
            </div>
          ) : null}

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <Button
              text={isUploading ? "Transfert…" : "Sauvegarder"}
              icon={FaSave}
              onClick={onSave}
              type="button"
              className="!rounded-xl gap-2"
              isLoading={isUploading}
              disabled={!selectedImage || isUploading}
            />

            {selectedImage ? (
              <Button
                text="Annuler"
                icon={FaTimes}
                type="button"
                onClick={() => onSelect(null)}
                className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
                disabled={isUploading}
              />
            ) : null}
          </div>

          <div className="mt-4 flex items-start gap-2 rounded-2xl bg-slate-50 px-3 py-3 text-xs leading-5 text-slate-500 dark:bg-slate-950/70 dark:text-slate-400">
            <FaCloudUploadAlt className="mt-0.5 shrink-0 text-slate-400" />
            <p>
              Formats acceptés : JPG, PNG, WebP. Taille maximale : 10 Mo.
              L’image sera convertie en WebP lors de l’envoi.
            </p>
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
            Aperçu
          </p>

          <div className="overflow-hidden rounded-2xl bg-slate-100 shadow-sm ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
            <img
              src={previewSrc}
              alt={`${title} — aperçu`}
              className={`w-full object-cover ${
                featured ? "h-56 xl:h-64" : "h-52"
              }`}
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
              onError={handleImageFallback}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReglementInterieurForm;